import AiIcon from "../../Core/AiIcon/AiIcon";
import {
	ArrowDown,
	ArrowLeft,
	ArrowRight,
	ArrowRightLeft,
	ArrowUp,
	CalendarClock,
	Code2,
	Copy,
	GripVertical,
	History,
	Move,
	Maximize2,
	MoreHorizontal,
	Pencil,
	RefreshCw,
	Settings2,
	Trash2,
} from "lucide-react";
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Skeleton } from "../../ui/skeleton";
import {
	BucketSetting,
	CHART_TYPE_LABELS,
	CHART_TYPES,
	COMPARISON_OPTIONS,
	ChartType,
	ComparisonMode,
	GRAIN_LABELS,
	GRAINS,
	ReportDashboard,
	ReportFilters,
	ReportWidget,
	WidgetDisplay,
	WidgetPeriod,
	isNote,
	widgetTitle,
} from "../../../models/ReportBuilder";
import { useExplainWidget, useWidgetData } from "../../../networking/report-builder.hooks";
import { useReportBuilderStore } from "../../../hooks/reportBuilder.hooks";
import {
	DATE_RANGE_PRESETS,
	effectiveGrain,
	formatRangeShort,
	presetLabel,
	resolveDateRange,
} from "./dateRange";
import NoteDialog, { NoteContent } from "./NoteWidget";
import { afterMenuClose } from "./afterMenuClose";
import { useReportFit, useWidgetDragHandle } from "./ReportRows";
import { MoveDirection, canMove } from "./rowLayout";
import InlineEditText from "./InlineEditText";
import WidgetChart, { ChartPoint } from "./WidgetChart";
import WidgetMaximizeDialog from "./WidgetMaximizeDialog";
import WidgetSettingsDialog from "./WidgetSettingsDialog";

const FOLLOW_REPORT = "__report";

/** Menu fallback for dragging: left/right swap in the row, up/down go to the row above/below. */
const MOVE_OPTIONS: { direction: MoveDirection; label: string; Icon: typeof ArrowUp }[] = [
	{ direction: "up", label: "Upp", Icon: ArrowUp },
	{ direction: "down", label: "Ner", Icon: ArrowDown },
	{ direction: "left", label: "Vänster", Icon: ArrowLeft },
	{ direction: "right", label: "Höger", Icon: ArrowRight },
];
const STALE_AFTER_MS = 6 * 60 * 60 * 1000;

interface WidgetCardProps {
	dashboard: ReportDashboard;
	widget: ReportWidget;
	/** The report's resolved range. */
	range: { from: string; to: string };
	compare?: ComparisonMode;
	filters?: ReportFilters;
	/** Edit mode: drag handle and menu. */
	editable: boolean;
	/** Other reports, for "Flytta till". */
	otherDashboards: Pick<ReportDashboard, "id" | "name">[];
	onEditPrompt: (initialPrompt?: string) => void;
}

/** The comparison this widget runs with: its own override, or the report's. */
export const resolveCompare = (
	display: WidgetDisplay | undefined,
	report?: ComparisonMode
): ComparisonMode | undefined =>
	!display?.compare || display.compare === "report"
		? report
		: display.compare === "none"
		? undefined
		: display.compare;

/** One widget on the report: title, chart for the effective range, and the edit menu. */
const WidgetCard: React.FC<WidgetCardProps> = ({
	dashboard,
	widget,
	range,
	compare,
	filters,
	editable,
	otherDashboards,
	onEditPrompt,
}) => {
	const store = useReportBuilderStore();
	const dragHandle = useWidgetDragHandle();
	const [showSql, setShowSql] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [maximized, setMaximized] = useState<{ open: boolean; point?: ChartPoint }>({
		open: false,
	});
	const [noteOpen, setNoteOpen] = useState(false);
	const [explanation, setExplanation] = useState<string>();
	const explain = useExplainWidget();
	const note = isNote(widget);

	// A pinned period overrides the report's range for this widget only.
	const period = widget.spec.period;
	const effectiveRange = period ? resolveDateRange({ preset: period }) : range;
	const bucket: BucketSetting = widget.spec.bucket ?? "auto";
	const grain = effectiveGrain(bucket, effectiveRange);
	const effectiveCompare = widget.spec.usesDateRange
		? resolveCompare(widget.display, compare)
		: undefined;
	const refetchIntervalMs = dashboard.autoRefreshMinutes
		? dashboard.autoRefreshMinutes * 60 * 1000
		: undefined;
	const { data, isLoading, isFetching, error, refetch } = useWidgetData(
		note
			? undefined
			: {
					sql: widget.spec.sql,
					from: effectiveRange.from,
					to: effectiveRange.to,
					compare: effectiveCompare,
					bucket,
					filters,
			  },
		{ refetchIntervalMs }
	);
	const { title, description } = widgetTitle(widget);
	/** Condensed TV mode: a number tile puts its title beside the number and drops the footer. */
	const condensedTile = useReportFit().condensed && !note && widget.spec.chart.type === "number";
	const stale = data?.dataAsOf
		? Date.now() - new Date(data.dataAsOf).getTime() > STALE_AFTER_MS
		: false;
	const includesToday =
		grain === "day" &&
		widget.spec.usesBucket &&
		effectiveRange.to >= new Date().toISOString().substring(0, 10);
	const errorText = error?.error?.detail || error?.error?.title || "Frågan misslyckades";
	const filtersActive = !!filters && Object.values(filters).some((v) => v?.length);

	const update = (patch: Parameters<typeof store.updateWidget>[2]) =>
		store.updateWidget(dashboard.id, widget.id, patch);
	const setSpec = (patch: Partial<ReportWidget["spec"]>) =>
		update({ spec: { ...widget.spec, ...patch } });
	/** Inline title/subtitle edit. Empty or equal to the generated text clears the override, like the settings dialog. */
	const setDisplayText = (key: "title" | "description", value: string) => {
		const generated = key === "title" ? widget.spec.title : widget.spec.description;
		const display: WidgetDisplay = { ...(widget.display ?? {}) };
		if (value && value !== generated) display[key] = value;
		else delete display[key];
		update({ display: Object.keys(display).length ? display : undefined });
	};

	const periodText = !widget.spec.usesDateRange
		? "Oberoende av period"
		: period
		? `${presetLabel(period)} (${formatRangeShort(effectiveRange)})`
		: "Följer dashboarden";

	return (
		<div
			className={cn(
				"flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm",
				condensedTile && "flex-row items-center gap-4 py-3",
				editable && "border-dashed border-gray-300",
				note && "bg-transparent shadow-none",
				note && !editable && "border-transparent"
			)}
		>
			<div
				className={cn(
					"mb-2 flex items-start justify-between gap-2",
					note && "mb-0",
					condensedTile && "mb-0 min-w-0 flex-1"
				)}
			>
				{editable && (
					<button
						type="button"
						ref={dragHandle?.ref}
						{...(dragHandle?.attributes ?? {})}
						{...(dragHandle?.listeners ?? {})}
						className="-ml-2 mt-0.5 cursor-grab rounded text-gray-400 hover:text-gray-600 active:cursor-grabbing"
						title="Dra för att flytta"
						aria-label="Flytta widgeten"
					>
						<GripVertical className="h-4 w-4" />
					</button>
				)}
				{!note && (
					<div className="min-w-0 flex-1">
						<div className="flex min-w-0 items-center gap-1.5">
							<InlineEditText
								as="h3"
								value={title}
								editable={editable}
								className="min-w-0 truncate text-sm font-semibold text-gray-900"
								title={title}
								editHint="Dubbelklicka för att ändra rubrik"
								onSave={(v) => setDisplayText("title", v)}
							/>
							{period && (
								<span
									className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-action/10 px-1.5 py-0.5 text-[10px] font-medium text-action"
									title={`Låst period: ${periodText}`}
								>
									<CalendarClock className="h-3 w-3" /> {presetLabel(period)}
								</span>
							)}
							{widget.spec.usesBucket && bucket !== "auto" && (
								<span
									className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600"
									title="Låst gruppering"
								>
									per {GRAIN_LABELS[bucket].toLowerCase()}
								</span>
							)}
						</div>
						<InlineEditText
							value={description ?? ""}
							placeholder="Underrubrik"
							editable={editable}
							className="truncate text-xs text-gray-500"
							editHint="Dubbelklicka för att ändra underrubrik"
							onSave={(v) => setDisplayText("description", v)}
						/>
					</div>
				)}
				{note && <div className="min-w-0 flex-1" />}
				<div className="flex items-center gap-1">
					{isFetching && !isLoading && (
						<span className="text-[10px] text-gray-400">uppdaterar…</span>
					)}
					{!note && !editable && !condensedTile && (
						<button
							type="button"
							onClick={() => setMaximized({ open: true })}
							className="rounded p-1 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-700"
							aria-label="Maximera"
							title="Maximera"
						>
							<Maximize2 className="h-3.5 w-3.5" />
						</button>
					)}
					{editable && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									type="button"
									className="rounded p-1 text-gray-500 hover:bg-gray-100"
									aria-label="Widgetmeny"
								>
									<MoreHorizontal className="h-4 w-4" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="z-[1300] w-60">
								{note ? (
									<DropdownMenuItem onSelect={() => afterMenuClose(() => setNoteOpen(true))}>
										<Pencil className="mr-2 h-4 w-4" /> Ändra text…
									</DropdownMenuItem>
								) : (
									<>
										<DropdownMenuItem onSelect={() => afterMenuClose(() => onEditPrompt())}>
											<AiIcon className="mr-2 h-4 w-4" /> Ändra med prompt…
										</DropdownMenuItem>
										<DropdownMenuItem onSelect={() => afterMenuClose(() => setSettingsOpen(true))}>
											<Settings2 className="mr-2 h-4 w-4" /> Rubrik, format och utseende…
										</DropdownMenuItem>
										<DropdownMenuItem
											onSelect={() => afterMenuClose(() => setMaximized({ open: true }))}
										>
											<Maximize2 className="mr-2 h-4 w-4" /> Maximera
										</DropdownMenuItem>
										<DropdownMenuSeparator />
										<DropdownMenuSub>
											<DropdownMenuSubTrigger>Diagramtyp</DropdownMenuSubTrigger>
											<DropdownMenuSubContent className="z-[1300]">
												<DropdownMenuRadioGroup
													value={widget.spec.chart.type}
													onValueChange={(v) =>
														setSpec({ chart: { ...widget.spec.chart, type: v as ChartType } })
													}
												>
													{CHART_TYPES.map((t) => (
														<DropdownMenuRadioItem key={t} value={t}>
															{CHART_TYPE_LABELS[t]}
														</DropdownMenuRadioItem>
													))}
												</DropdownMenuRadioGroup>
											</DropdownMenuSubContent>
										</DropdownMenuSub>
										<DropdownMenuSub>
											<DropdownMenuSubTrigger>Tid</DropdownMenuSubTrigger>
											<DropdownMenuSubContent className="z-[1300] max-h-[70vh] overflow-y-auto">
												<DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-gray-400">
													Period
												</DropdownMenuLabel>
												<DropdownMenuRadioGroup
													value={period ?? FOLLOW_REPORT}
													onValueChange={(v) =>
														setSpec({
															period: v === FOLLOW_REPORT ? undefined : (v as WidgetPeriod),
														})
													}
												>
													<DropdownMenuRadioItem value={FOLLOW_REPORT}>
														Följ dashboarden
													</DropdownMenuRadioItem>
													{DATE_RANGE_PRESETS.filter((p) => p.value !== "custom").map((p) => (
														<DropdownMenuRadioItem key={p.value} value={p.value}>
															{p.label}
														</DropdownMenuRadioItem>
													))}
												</DropdownMenuRadioGroup>
												{widget.spec.usesBucket && (
													<>
														<DropdownMenuSeparator />
														<DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-gray-400">
															Gruppering
														</DropdownMenuLabel>
														<DropdownMenuRadioGroup
															value={bucket}
															onValueChange={(v) =>
																setSpec({ bucket: v === "auto" ? undefined : (v as BucketSetting) })
															}
														>
															<DropdownMenuRadioItem value="auto">
																Automatisk (nu: {GRAIN_LABELS[grain].toLowerCase()})
															</DropdownMenuRadioItem>
															{GRAINS.map((g) => (
																<DropdownMenuRadioItem key={g} value={g}>
																	{GRAIN_LABELS[g]}
																</DropdownMenuRadioItem>
															))}
														</DropdownMenuRadioGroup>
													</>
												)}
												{widget.spec.usesDateRange && (
													<>
														<DropdownMenuSeparator />
														<DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-gray-400">
															Jämförelse
														</DropdownMenuLabel>
														<DropdownMenuRadioGroup
															value={widget.display?.compare ?? "report"}
															onValueChange={(v) =>
																store.setDisplay(dashboard.id, widget.id, {
																	compare:
																		v === "report" ? undefined : (v as WidgetDisplay["compare"]),
																})
															}
														>
															<DropdownMenuRadioItem value="report">
																Följ dashboarden
															</DropdownMenuRadioItem>
															{COMPARISON_OPTIONS.map((o) => (
																<DropdownMenuRadioItem key={o.value} value={o.value}>
																	{o.label}
																</DropdownMenuRadioItem>
															))}
														</DropdownMenuRadioGroup>
													</>
												)}
											</DropdownMenuSubContent>
										</DropdownMenuSub>
										{!!widget.history?.length && (
											<DropdownMenuSub>
												<DropdownMenuSubTrigger>
													<History className="mr-2 h-4 w-4" /> Historik
												</DropdownMenuSubTrigger>
												<DropdownMenuSubContent className="z-[1300] max-w-xs">
													{[...widget.history].reverse().map((entry, i) => (
														<DropdownMenuItem
															key={i}
															onSelect={() =>
																store.regenerateWidget(
																	dashboard.id,
																	widget.id,
																	entry.spec,
																	entry.prompt
																)
															}
															className="flex-col items-start"
														>
															<span className="text-[10px] text-gray-400">
																{new Date(entry.at).toLocaleString("sv-SE")}
															</span>
															<span className="line-clamp-2 text-xs">
																{entry.prompt || entry.spec.title}
															</span>
														</DropdownMenuItem>
													))}
												</DropdownMenuSubContent>
											</DropdownMenuSub>
										)}
										<DropdownMenuSeparator />
										<DropdownMenuItem
											onSelect={() => {
												setExplanation("…");
												explain.mutate(
													{ spec: widget.spec },
													{
														onSuccess: (r) => setExplanation(r.explanation),
														onError: (e) =>
															setExplanation(e.error?.detail || "Kunde inte förklara widgeten."),
													}
												);
											}}
										>
											<AiIcon className="mr-2 h-4 w-4" /> Förklara widgeten
										</DropdownMenuItem>
										<DropdownMenuItem onSelect={() => refetch()}>
											<RefreshCw className="mr-2 h-4 w-4" /> Uppdatera data
										</DropdownMenuItem>
										<DropdownMenuItem onSelect={() => setShowSql((s) => !s)}>
											<Code2 className="mr-2 h-4 w-4" /> {showSql ? "Dölj SQL" : "Visa SQL"}
										</DropdownMenuItem>
									</>
								)}
								<DropdownMenuSeparator />
								<DropdownMenuItem onSelect={() => store.duplicateWidget(dashboard.id, widget.id)}>
									<Copy className="mr-2 h-4 w-4" /> Duplicera
								</DropdownMenuItem>
								<DropdownMenuSub>
									<DropdownMenuSubTrigger>
										<Move className="mr-2 h-4 w-4" /> Flytta
									</DropdownMenuSubTrigger>
									<DropdownMenuSubContent className="z-[1300]">
										{MOVE_OPTIONS.map(({ direction, label, Icon }) => (
											<DropdownMenuItem
												key={direction}
												disabled={!canMove(dashboard.rows, widget.id, direction)}
												onSelect={() => store.moveWidget(dashboard.id, widget.id, direction)}
											>
												<Icon className="mr-2 h-4 w-4" /> {label}
											</DropdownMenuItem>
										))}
									</DropdownMenuSubContent>
								</DropdownMenuSub>
								{otherDashboards.length > 0 && (
									<DropdownMenuSub>
										<DropdownMenuSubTrigger>
											<ArrowRightLeft className="mr-2 h-4 w-4" /> Flytta till dashboard
										</DropdownMenuSubTrigger>
										<DropdownMenuSubContent className="z-[1300]">
											{otherDashboards.map((d) => (
												<DropdownMenuItem
													key={d.id}
													onSelect={() =>
														store.moveWidgetToDashboard(dashboard.id, widget.id, d.id)
													}
												>
													{d.name}
												</DropdownMenuItem>
											))}
										</DropdownMenuSubContent>
									</DropdownMenuSub>
								)}
								{!note && widget.prompt && (
									<DropdownMenuLabel className="text-[10px] font-normal text-gray-400">
										Prompt: {widget.prompt}
									</DropdownMenuLabel>
								)}
								<DropdownMenuItem
									onSelect={() => store.removeWidget(dashboard.id, widget.id)}
									className="text-red-600"
								>
									<Trash2 className="mr-2 h-4 w-4" /> Ta bort
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</div>

			{note ? (
				<div className="min-h-0 flex-1 overflow-auto">
					<NoteContent text={widget.note ?? ""} />
				</div>
			) : (
				<div className={cn("min-h-0 flex-1", condensedTile && "flex-none")}>
					{isLoading && <Skeleton className={cn("h-full w-full", condensedTile && "h-10 w-32")} />}
					{error && (
						<div className="flex h-full flex-col gap-2 rounded bg-red-50 p-3 text-xs text-red-700">
							<span className="break-words">{errorText}</span>
							{editable && (
								<button
									type="button"
									onClick={() =>
										onEditPrompt(
											`Frågan ger felet "${errorText}". Rätta frågan så att den fungerar, behåll vad widgeten ska visa: ${
												widget.prompt || widget.spec.title
											}`
										)
									}
									className="inline-flex w-fit items-center gap-1 rounded-md bg-white px-2 py-1 font-medium text-gray-800 shadow-sm hover:bg-gray-50"
								>
									<AiIcon className="h-3 w-3" /> Fixa med AI
								</button>
							)}
						</div>
					)}
					{data && !error && (
						<WidgetChart
							spec={widget.spec}
							data={data}
							display={widget.display}
							height="100%"
							layout={condensedTile ? "row" : "tile"}
							onPointClick={editable ? undefined : (point) => setMaximized({ open: true, point })}
						/>
					)}
				</div>
			)}

			{!note && !condensedTile && (
				<div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-gray-400">
					<span className={cn("truncate", period && "font-medium text-action")}>
						{periodText}
						{widget.spec.usesBucket ? ` · per ${GRAIN_LABELS[grain].toLowerCase()}` : ""}
						{data?.comparison ? ` · jämfört med ${formatRangeShort(data.comparison.range)}` : ""}
						{filtersActive ? (widget.spec.usesFilters ? " · filtrerad" : "") : ""}
						{filtersActive && !widget.spec.usesFilters && (
							<span
								className="text-warning"
								title="Frågan saknar :filter(...). Ändra med prompt så läggs filtren in."
							>
								{" "}
								· påverkas inte av filtret
							</span>
						)}
					</span>
					<span
						className={cn("shrink-0", stale && "font-medium text-warning")}
						title={
							includesToday ? "Dagens siffror är ofullständiga tills nästa inläsning" : undefined
						}
					>
						{includesToday ? "idag ofullständig · " : ""}
						{data?.dataAsOf
							? `Data från ${new Date(data.dataAsOf).toLocaleString("sv-SE", {
									dateStyle: "short",
									timeStyle: "short",
							  })}`
							: ""}
						{data?.truncated ? " · avkortat" : ""}
					</span>
				</div>
			)}
			{showSql && (
				<pre className="mt-2 max-h-40 overflow-auto rounded bg-gray-900 p-2 text-[11px] leading-snug text-gray-100">
					{widget.spec.sql}
				</pre>
			)}
			{explanation && (
				<div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
					<div className="mb-1 flex items-center justify-between">
						<span className="font-medium text-gray-900">Förklaring</span>
						<button
							type="button"
							onClick={() => setExplanation(undefined)}
							className="text-gray-400 hover:text-gray-700"
						>
							stäng
						</button>
					</div>
					{explain.isLoading ? "Tänker…" : explanation}
				</div>
			)}

			{!note && (
				<WidgetSettingsDialog
					open={settingsOpen}
					onOpenChange={setSettingsOpen}
					widget={widget}
					data={data}
					onSave={(display) =>
						update({ display: Object.keys(display).length ? display : undefined })
					}
				/>
			)}
			{!note && (
				<WidgetMaximizeDialog
					open={maximized.open}
					onOpenChange={(open) => setMaximized((m) => ({ ...m, open }))}
					widget={widget}
					data={data}
					periodLabel={`${periodText}${
						widget.spec.usesBucket ? ` · per ${GRAIN_LABELS[grain].toLowerCase()}` : ""
					}`}
					initialPoint={maximized.point}
				/>
			)}
			{note && (
				<NoteDialog
					open={noteOpen}
					onOpenChange={setNoteOpen}
					initialText={widget.note}
					onSave={(text) => update({ note: text })}
				/>
			)}
		</div>
	);
};

export default WidgetCard;
